import {
  FC,
  createContext,
  useContext,
  useState,
  useRef,
  useCallback,
  useEffect,
} from 'react';
import { useSnackbar } from 'notistack';

import { PlanFeaturesTypes } from '~globals/types/enums';
import {
  DataPaginateResponse,
  PaginationRequestParams,
  ErrorResponse,
} from '~globals/types/api';
import { Nullable } from '~globals/types/commons';
import {
  useSubscriptionFeatures,
  useParseFiltersRequestSearchItems,
  useSelectionIds,
  useToggle,
  useDataTableRequest,
  useLazyRequest,
  useToggleDialogConfirmation,
} from '~hooks/index';
import { ItemsSearchFilters } from '~hooks/useItemsSearchFilters';
import { searchItemsV2, searchItemsMapV2 } from '~services/item';
import {
  ItemFilterV2Base,
  ItemExtended,
  ItemMap,
  ItemsSearchHead,
} from '~services/item/types';
import { searchWarehouseMap } from '~services/warehouse';
import { WarehouseMap } from '~services/warehouse/types';
import { getFeatureAvailableToDate } from '~utils/feature';

import { useDashboardVariantV2CarrierContext } from '../../../Context';
// eslint-disable-next-line max-len
import { useDashboardVariantV2CarrierInWarehouseFiltersContext } from '../../../InWarehouseFiltersContext';

import {
  DashboardVariantV2CarrierContentInWarehouseTabContextValue,
  DashboardVariantV2CarrierContentInWarehouseTabDialogDetailsOrderData,
  DashboardVariantV2CarrierContentInWarehouseTabDialogChangeStateOrderData,
} from './types';
import {
  ITEM_KEY_ID,
  DEFAULT_PAGINATION,
  DEFAULT_FILTERS_PAYLOAD,
  DEFAULT_ROUTE_ITEM_STATES,
  DEFAULT_COLLECT_ITEM_STATES,
} from './utils';

const DashboardVariantV2CarrierContentInWarehouseTabContext =
  createContext<DashboardVariantV2CarrierContentInWarehouseTabContextValue>({
    parseFiltersRequest: () => DEFAULT_FILTERS_PAYLOAD,
    selection: {
      selectedIds: [],
      utils: {
        add: () => {},
        addMultiple: () => {},
        toggle: () => {},
        deleteOne: () => {},
        deleteMultiple: () => {},
        deleteCurrentAndAfter: () => {},
        has: () => false,
        isEmpty: () => true,
        reset: () => {},
      },
    },
    itemsWithGeoErrors: 0,
    carrierCompaniesCount: 0,
    table: {
      data: [],
      stats: null,
      loading: true,
      error: null,
      reloadRequest: () => {},
      totalSize: 0,
      queryParams: {
        pagination: DEFAULT_PAGINATION,
        filters: DEFAULT_FILTERS_PAYLOAD,
      },
      showTable: {
        open: true,
        toggleOpen: () => {},
      },
      handleInitialData: () => {},
      handleChangeTable: () => {},
      handleInitialTable: () => {},
    },
    map: {
      enabled: false,
      showMap: {
        open: false,
        toggleOpen: () => {},
      },
      data: {
        orders: {
          data: [],
          loading: true,
          reloadRequest: () => {},
        },
        warehouses: {
          data: [],
          loading: true,
          reloadRequest: () => {},
        },
      },
    },
    isOnlyTodayDelivery: {
      value: false,
      toggleValue: () => {},
    },
    actions: {
      reloadDataAndResetSelectedRows: () => {},
      handleForceRequests: () => {},
    },
    dialogs: {
      confirmDeleteOrder: {
        data: null,
        open: false,
        handleOpen: () => {},
        handleClose: () => {},
      },
      cancelOrder: {
        data: null,
        open: false,
        handleOpen: () => {},
        handleClose: () => {},
      },
      detailsOrder: {
        data: null,
        open: false,
        handleOpen: () => {},
        handleClose: () => {},
      },
      changeStateOrder: {
        data: null,
        open: false,
        handleOpen: () => {},
        handleClose: () => {},
      },
      modifyAssignmentOrder: {
        data: null,
        open: false,
        handleOpen: () => {},
        handleClose: () => {},
      },
    },
  });

export const useDashboardVariantV2CarrierContentInWarehouseTabContext =
  (): DashboardVariantV2CarrierContentInWarehouseTabContextValue =>
    useContext(DashboardVariantV2CarrierContentInWarehouseTabContext);

const DashboardVariantV2CarrierContentInWarehouseTabProvider: FC = ({
  children,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { hasAccessToFeature } = useSubscriptionFeatures();

  const {
    activeTab: { currentTab },
  } = useDashboardVariantV2CarrierContext();
  const { filters, forceRequest } =
    useDashboardVariantV2CarrierInWarehouseFiltersContext();

  const parseFiltersRequest = useParseFiltersRequestSearchItems({
    filters: filters.filters,
    activeFilterTabId: currentTab,
    defaultRouteItemState: DEFAULT_ROUTE_ITEM_STATES,
    defaultCollectItemState: DEFAULT_COLLECT_ITEM_STATES,
  });

  const [isOnlyTodayDelivery, toggleIsOnlyTodayDelivery] = useToggle(false);

  const selection = useSelectionIds<string>([]);

  const [showMap, toogleShowMap] = useToggle(
    hasAccessToFeature(
      PlanFeaturesTypes.MgmItemMap,
      getFeatureAvailableToDate(PlanFeaturesTypes.MgmItemMap),
    ),
  );

  const showMapRef = useRef(showMap);
  showMapRef.current = showMap;

  const [showTable, toggleShowTable] = useToggle(true);

  const [itemsWithGeoErrors, setItemsWithGeoErrors] = useState(0);
  const [carrierCompaniesCount, setCarrierCompaniesCount] = useState(0);
  const table = useDataTableRequest<
    PaginationRequestParams<ItemFilterV2Base>,
    DataPaginateResponse<ItemExtended[], null, ItemsSearchHead>,
    ErrorResponse
  >({
    request: searchItemsV2,
    payload: {
      pagination: DEFAULT_PAGINATION,
      filters: parseFiltersRequest(),
    },
    transformResponse: (res) => {
      const response = res.data as DataPaginateResponse<
        ItemExtended[],
        null,
        ItemsSearchHead
      >;

      setItemsWithGeoErrors(response.head.itemsWithGeoErrors ?? 0);
      setCarrierCompaniesCount(response.head.carrierCompaniesCount ?? 0);

      if (response.data.results) {
        if (
          response.data.pagination.offset === DEFAULT_PAGINATION.page &&
          response.data.results.length > 0 &&
          !showMapRef.current
        ) {
          const selectedIds = response.data.results.map(
            (item) => item[ITEM_KEY_ID],
          );

          selection.utils.reset();
          selection.utils.addMultiple(selectedIds);
        }
      }

      return response;
    },
  });

  const [mapOrders, setMapOrders] = useState<ItemMap[]>([]);
  const [, loadingMapOrdes, , executeGetMapOrders] = useLazyRequest({
    initialState: [],
    request: searchItemsMapV2,
    withPostSuccess: (response) => {
      const orders = response.data?.data.results as ItemMap[];

      setMapOrders(orders);
    },
    withPostFailure: () => {
      enqueueSnackbar('No se pudo cargar los datos', { variant: 'error' });
    },
  });

  const reloadMapOrders = useCallback(
    ({
      withInitialFilters,
      currentFilters,
    }: {
      withInitialFilters: boolean;
      currentFilters?: Partial<ItemsSearchFilters>;
    }) => {
      if (showMap) {
        setMapOrders([]);

        const filtersRequest = withInitialFilters
          ? DEFAULT_FILTERS_PAYLOAD
          : parseFiltersRequest(currentFilters);

        executeGetMapOrders(filtersRequest);
      }
    },
    [executeGetMapOrders, parseFiltersRequest, showMap],
  );

  const [mapWarehouses, setMapWarehouses] = useState<WarehouseMap[]>([]);
  const [, loadingMapWarehouses, , executeGetMapWarehouses] = useLazyRequest({
    initialState: [],
    request: searchWarehouseMap,
    withPostSuccess: (response) => {
      const warehouses = response.data?.data.results as WarehouseMap[];

      setMapWarehouses(warehouses);
    },
    withPostFailure: () => {
      enqueueSnackbar('No se pudo cargar los datos', { variant: 'error' });
    },
  });

  const reloadMapWarehouses = useCallback(() => {
    if (showMap) {
      setMapWarehouses([]);
      executeGetMapWarehouses(null);
    }
  }, [executeGetMapWarehouses, showMap]);

  useEffect(() => {
    reloadMapOrders({ withInitialFilters: false });
    reloadMapWarehouses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMap]);

  const handleForceRequests = useCallback(
    (currentFilters?: Partial<ItemsSearchFilters>) => {
      selection.utils.reset();

      reloadMapOrders({ withInitialFilters: false, currentFilters });

      reloadMapWarehouses();

      table.handleChangeTable('filter', {
        pagination: DEFAULT_PAGINATION,
        filters: parseFiltersRequest(currentFilters),
      });

      forceRequest.toggleValue(false);
    },
    [
      forceRequest,
      parseFiltersRequest,
      reloadMapOrders,
      reloadMapWarehouses,
      selection.utils,
      table,
    ],
  );

  useEffect(() => {
    if (forceRequest.value) {
      handleForceRequests();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceRequest.value]);

  const reloadDataAndResetSelectedRows = useCallback(() => {
    selection.utils.reset();

    if (table.queryParams.pagination.page === DEFAULT_PAGINATION.page) {
      table.reloadRequest();
    } else {
      table.handleChangeTable('filter', {
        pagination: table.queryParams.pagination,
        filters: parseFiltersRequest(),
      });
    }

    reloadMapOrders({ withInitialFilters: false });
  }, [selection.utils, table, reloadMapOrders, parseFiltersRequest]);

  const [
    openConfirmDeleteOrder,
    selectedOrderDelete,
    handleOpenConfirmDeleteOrder,
    handleCloseConfirmDeleteOrder,
  ] = useToggleDialogConfirmation<Nullable<ItemExtended>>(null);

  const [
    openDialogCancelOrder,
    selectedCancelOrder,
    handleOpenDialogCancelOrder,
    handleCloseDialogCancelOrder,
  ] = useToggleDialogConfirmation<Nullable<ItemExtended>>(null);

  const [
    openDialogDetailsOrder,
    selectedForDetailsOrder,
    handleOpenDialogDetailsOrder,
    handleCloseDialogDetailsOrder,
  ] =
    useToggleDialogConfirmation<
      Nullable<DashboardVariantV2CarrierContentInWarehouseTabDialogDetailsOrderData>
    >(null);

  const [
    openDialogChangeStateOrder,
    selectedOrderDataToChangeState,
    handleOpenDialogChangeStateOrder,
    handleCloseDialogChangeStateOrder,
  ] =
    useToggleDialogConfirmation<
      Nullable<DashboardVariantV2CarrierContentInWarehouseTabDialogChangeStateOrderData>
    >(null);

  const [
    openDialogModifyAssignmentOrder,
    selectedItemIdToModifyAssignment,
    handleOpenDialogModifyAssignmentOrder,
    handleCloseDialogModifyAssignmentOrder,
  ] = useToggleDialogConfirmation<Nullable<string>>(null);

  return (
    <DashboardVariantV2CarrierContentInWarehouseTabContext.Provider
      value={{
        parseFiltersRequest,
        selection,
        itemsWithGeoErrors,
        carrierCompaniesCount,
        table: {
          ...table,
          showTable: {
            open: showTable,
            toggleOpen: toggleShowTable,
          },
        },
        map: {
          enabled: true,
          showMap: {
            open: showMap,
            toggleOpen: toogleShowMap,
          },
          data: {
            orders: {
              data: mapOrders,
              loading: loadingMapOrdes,
              reloadRequest: reloadMapOrders,
            },
            warehouses: {
              data: mapWarehouses,
              loading: loadingMapWarehouses,
              reloadRequest: reloadMapWarehouses,
            },
          },
        },
        isOnlyTodayDelivery: {
          value: isOnlyTodayDelivery,
          toggleValue: toggleIsOnlyTodayDelivery,
        },
        actions: {
          reloadDataAndResetSelectedRows,
          handleForceRequests,
        },
        dialogs: {
          confirmDeleteOrder: {
            open: openConfirmDeleteOrder,
            data: selectedOrderDelete,
            handleOpen: handleOpenConfirmDeleteOrder,
            handleClose: handleCloseConfirmDeleteOrder,
          },
          cancelOrder: {
            open: openDialogCancelOrder,
            data: selectedCancelOrder,
            handleOpen: handleOpenDialogCancelOrder,
            handleClose: handleCloseDialogCancelOrder,
          },
          detailsOrder: {
            open: openDialogDetailsOrder,
            data: selectedForDetailsOrder,
            handleOpen: handleOpenDialogDetailsOrder,
            handleClose: handleCloseDialogDetailsOrder,
          },
          changeStateOrder: {
            open: openDialogChangeStateOrder,
            data: selectedOrderDataToChangeState,
            handleOpen: handleOpenDialogChangeStateOrder,
            handleClose: handleCloseDialogChangeStateOrder,
          },
          modifyAssignmentOrder: {
            open: openDialogModifyAssignmentOrder,
            data: selectedItemIdToModifyAssignment,
            handleOpen: handleOpenDialogModifyAssignmentOrder,
            handleClose: handleCloseDialogModifyAssignmentOrder,
          },
        },
      }}
    >
      {children}
    </DashboardVariantV2CarrierContentInWarehouseTabContext.Provider>
  );
};

export default DashboardVariantV2CarrierContentInWarehouseTabProvider;
